"use client";

import { CircleCheck, TriangleAlert } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import type {
  ClearanceLadderResult,
  DiscountTiers,
} from "@/lib/bf-toolkit/calculations";
import { clearanceLadder } from "@/lib/bf-toolkit/calculations";
import { cn } from "@/lib/utils";

import {
  dateFromWeeks,
  daysBetween,
  defaultTargetDate,
  fmtLong,
  today,
  toDateValue,
} from "./plan";
import {
  DEFAULTS,
  ELASTICITY_SLIDER,
  PERCENT_RANGE,
  SENSITIVITY_OPTIONS,
  STRINGS,
} from "./strings";

interface ClearanceLadderProps {
  /** Request locale, used only for currency and number formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the input prefix (e.g. "$"). */
  currencySymbol: string;
}

// Bump the number text well above the Input default so the figures a founder
// types read clearly. twMerge resolves the font-size conflict.
const INPUT_TEXT = "text-heading-20";

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/**
 * Parse a number the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can show a neutral prompt
 * rather than a bogus result.
 */
function parseNumber(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

export function ClearanceLadder({
  locale,
  currency,
  currencySymbol,
}: ClearanceLadderProps) {
  const [unitsRemainingStr, setUnitsRemainingStr] = useState<string>(
    DEFAULTS.unitsRemaining
  );
  const [sellingPriceStr, setSellingPriceStr] = useState<string>(
    DEFAULTS.sellingPrice
  );
  const [unitCostStr, setUnitCostStr] = useState<string>(DEFAULTS.unitCost);
  const [sellThroughStr, setSellThroughStr] = useState<string>(
    DEFAULTS.sellThrough
  );
  const [targetDate, setTargetDate] = useState<string>("");
  const [elasticity, setElasticity] = useState<number>(
    SENSITIVITY_OPTIONS.find((o) => o.value === DEFAULTS.sensitivity)
      ?.elasticity ?? 1.5
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tiers, setTiers] = useState<DiscountTiers>(DEFAULTS.tiers);
  const [holdingCostStr, setHoldingCostStr] = useState<string>(
    DEFAULTS.holdingCost
  );

  // Date-dependent state is resolved on the client only, so the server render
  // (which has no stable "today") never mismatches on hydration.
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    setNow(today());
    setTargetDate(toDateValue(defaultTargetDate()));
  }, []);

  const unitsRemaining = parseNumber(unitsRemainingStr);
  const sellingPrice = parseNumber(sellingPriceStr);
  const unitCost = parseNumber(unitCostStr);
  const sellThrough = parseNumber(sellThroughStr);
  const holdingCost =
    holdingCostStr.trim() === "" ? undefined : parseNumber(holdingCostStr);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
    return (value: number) => nf.format(value);
  }, [locale, currency]);

  const formatUnits = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return (value: number) => nf.format(Math.round(value));
  }, [locale]);

  const state = useMemo(() => {
    if (!now || !targetDate) return { kind: "empty" as const };

    const targetDateObj = new Date(`${targetDate}T00:00:00`);
    if (Number.isNaN(targetDateObj.getTime()))
      return { kind: "empty" as const };

    const weeksToTarget = daysBetween(now, targetDateObj) / 7;

    const result = clearanceLadder(
      unitsRemaining,
      unitCost,
      sellingPrice,
      weeksToTarget,
      sellThrough,
      elasticity,
      tiers,
      holdingCost !== undefined && Number.isFinite(holdingCost)
        ? holdingCost
        : undefined
    );

    if (result.status === "invalid") return { kind: "empty" as const };
    if (result.status === "already-cleared") {
      return { kind: "already-cleared" as const };
    }
    if (result.status === "past-target") {
      return { kind: "past-target" as const };
    }

    return {
      kind: "ok" as const,
      result,
      weeksToTarget,
      targetDateObj,
      now,
    };
  }, [
    now,
    targetDate,
    unitsRemaining,
    sellingPrice,
    unitCost,
    sellThrough,
    elasticity,
    tiers,
    holdingCost,
  ]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real schedule. Raw money/unit figures are intentionally not
  // sent, only the shape of the schedule.
  const posthog = usePostHog();
  const weeksForAnalytics = state.kind === "ok" ? state.weeksToTarget : null;
  const clearsForAnalytics =
    state.kind === "ok" ? state.result.clearsByTarget : null;
  const escalationsForAnalytics =
    state.kind === "ok" ? state.result.escalations : null;
  // Skip the first time the calculation becomes available: that's the
  // default-values render on mount, not a user-driven calculation.
  const hasCalculatedRef = useRef(false);
  useEffect(() => {
    if (weeksForAnalytics === null) return;
    if (!hasCalculatedRef.current) {
      hasCalculatedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "clearance-ladder",
        weeksToTarget: Math.round(weeksForAnalytics),
        elasticity,
        tiers,
        clearsByTarget: clearsForAnalytics,
        escalations: escalationsForAnalytics,
        hadHoldingCost:
          holdingCost !== undefined && Number.isFinite(holdingCost),
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [
    posthog,
    weeksForAnalytics,
    clearsForAnalytics,
    escalationsForAnalytics,
    elasticity,
    tiers,
    holdingCost,
  ]);

  const isWarning = state.kind === "past-target";

  // Which discount steps the schedule actually reaches. A step the plan never
  // needs (because an earlier step already clears the stock, or you're on pace)
  // greys out so the operator sees it isn't in play.
  const activeTiers =
    state.kind === "ok"
      ? new Set(state.result.phases.map((p) => p.tier))
      : new Set<string>(["start", "mid", "final"]);

  const startUplift = useMemo(() => {
    // A live, plain-language read of the assumption at the starting discount,
    // so the elasticity setting is never an invisible knob.
    if (sellThrough <= 0 || !Number.isFinite(sellThrough)) return null;
    const mult =
      (1 - Math.min(tiers.start, 99) / 100) ** -Math.max(elasticity, 0);
    return Math.round((mult - 1) * 100);
  }, [sellThrough, tiers.start, elasticity]);

  // The discount at which the sale price meets unit cost, i.e. the gross-margin
  // percentage. Beyond it every unit sells below cost. Marked on the tier
  // sliders so the operator can see where they cross that line. Null when it
  // can't be placed within the 0-100 track (no margin, or missing inputs).
  const atCostPct =
    Number.isFinite(sellingPrice) &&
    Number.isFinite(unitCost) &&
    sellingPrice > 0 &&
    unitCost >= 0 &&
    unitCost < sellingPrice
      ? (1 - unitCost / sellingPrice) * 100
      : null;

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      {/* Numbers (2 x 3) beside discount steps — similar heights keep the
          whitespace down; the sensitivity strip sits underneath. */}
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-10">
        <div>
          <h2 className="text-heading-16 mb-4 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <NumberField
              label={STRINGS.unitsRemaining}
              value={unitsRemainingStr}
              onChange={setUnitsRemainingStr}
            />
            <DateField
              label={STRINGS.targetDate}
              value={targetDate}
              onChange={setTargetDate}
            />
            <MoneyField
              label={STRINGS.sellingPrice}
              value={sellingPriceStr}
              symbol={currencySymbol}
              onChange={setSellingPriceStr}
            />
            <MoneyField
              label={STRINGS.unitCost}
              value={unitCostStr}
              symbol={currencySymbol}
              onChange={setUnitCostStr}
            />
            <NumberField
              label={STRINGS.sellThrough}
              hint={STRINGS.sellThroughHint}
              value={sellThroughStr}
              onChange={setSellThroughStr}
            />
            <MoneyField
              label={STRINGS.holdingCost}
              hint={STRINGS.holdingCostHint}
              value={holdingCostStr}
              symbol={currencySymbol}
              placeholder={STRINGS.holdingCostPlaceholder}
              onChange={setHoldingCostStr}
            />
          </div>
        </div>

        <div>
          <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
            {STRINGS.tiersLabel}
          </h2>
          <p className="text-body-14 text-text-label mt-1">
            {STRINGS.tiersHint}
          </p>
          <div className="mt-4 flex flex-col gap-5">
            <TierField
              label={STRINGS.startTier}
              value={tiers.start}
              atCostPct={atCostPct}
              onChange={(v) => setTiers((t) => ({ ...t, start: v }))}
            />
            <TierField
              label={STRINGS.midTier}
              value={tiers.mid}
              inactive={!activeTiers.has("mid")}
              atCostPct={atCostPct}
              onChange={(v) => setTiers((t) => ({ ...t, mid: v }))}
            />
            <TierField
              label={STRINGS.finalTier}
              value={tiers.final}
              inactive={!activeTiers.has("final")}
              atCostPct={atCostPct}
              onChange={(v) => setTiers((t) => ({ ...t, final: v }))}
            />
          </div>
        </div>
      </div>

      {/* Markdown strategy (price sensitivity): compact strip underneath. */}
      <div className="border-grey-100 mt-6 border-t pt-5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-body-sm font-semibold text-[#0D0D0D]">
            {STRINGS.sensitivityLabel}
          </span>
          <div
            role="radiogroup"
            aria-label={STRINGS.sensitivityLabel}
            className="flex flex-wrap gap-2"
          >
            {SENSITIVITY_OPTIONS.map((opt) => {
              const active = Math.abs(elasticity - opt.elasticity) < 1e-9;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setElasticity(opt.elasticity)}
                  className={cn(
                    "text-body-14 focus-visible:ring-pulse-500 cursor-pointer rounded-full border px-4 py-1.5 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
                    active
                      ? "border-pulse-700 bg-pulse-700 text-white"
                      : "border-grey-100 text-grey-700 hover:text-black"
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
            className="text-body-14 text-pulse-700 cursor-pointer font-medium underline underline-offset-4 hover:text-blue-800"
          >
            {STRINGS.advancedToggle}
          </button>
        </div>
        {startUplift !== null && (
          <p className="text-body-14 text-text-label mt-2">
            {STRINGS.sensitivityHint} At {Math.round(tiers.start)}% off we
            assume weekly sales rise about {startUplift}%.
          </p>
        )}
        {showAdvanced && (
          <div className="mt-3 max-w-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-body-14 text-text-label">
                {STRINGS.elasticityLabel}
              </span>
              <span className="text-body-14 font-semibold text-[#0D0D0D]">
                {elasticity.toFixed(1)}
              </span>
            </div>
            <SliderRoot
              aria-label={STRINGS.elasticityLabel}
              min={ELASTICITY_SLIDER.min}
              max={ELASTICITY_SLIDER.max}
              step={ELASTICITY_SLIDER.step}
              value={[elasticity]}
              onValueChange={(vals) =>
                setElasticity(
                  clamp(
                    vals[0] ?? ELASTICITY_SLIDER.min,
                    ELASTICITY_SLIDER.min,
                    ELASTICITY_SLIDER.max
                  )
                )
              }
              rangeClassName="bg-pulse-700"
              className="mt-2 py-2"
            >
              <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
            </SliderRoot>
          </div>
        )}
      </div>

      {/* Result, full width below the inputs */}
      <div
        aria-live="polite"
        className={cn(
          "mt-8 flex flex-col rounded-xl p-5 sm:p-6",
          isWarning ? "bg-red-50" : "bg-pulse-50"
        )}
      >
        {state.kind === "empty" && (
          <p className="text-body-16 text-text-label">{STRINGS.resultPrompt}</p>
        )}

        {state.kind === "past-target" && (
          <div>
            <div className="flex items-start gap-3">
              <TriangleAlert
                className="mt-1 size-6 shrink-0 text-red-600"
                aria-hidden="true"
              />
              <p className="text-heading-24 lg:text-heading-32 text-red-700">
                {STRINGS.pastTargetHeadline}
              </p>
            </div>
            <p className="text-body-16 mt-3 text-red-700">
              {STRINGS.pastTargetBody}
            </p>
          </div>
        )}

        {state.kind === "already-cleared" && (
          <div>
            <div className="flex items-start gap-3">
              <CircleCheck
                className="mt-1 size-6 shrink-0 text-green-700"
                aria-hidden="true"
              />
              <p className="text-heading-24 lg:text-heading-32 text-[#0D0D0D]">
                {STRINGS.alreadyClearedHeadline}
              </p>
            </div>
            <p className="text-body-16 text-text-label mt-3">
              {STRINGS.alreadyClearedBody}
            </p>
          </div>
        )}

        {state.kind === "ok" && (
          <Result
            result={state.result}
            unitsRemaining={unitsRemaining}
            unitCost={unitCost}
            tiers={tiers}
            weeksToTarget={state.weeksToTarget}
            targetDateObj={state.targetDateObj}
            now={state.now}
            formatCurrency={formatCurrency}
            formatUnits={formatUnits}
          />
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-2xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-5 w-auto shrink-0 md:h-6 md:w-auto"
        />
      </div>
    </div>
  );
}

interface CashComparison {
  cashLadder: number;
  cashBaseline: number;
  cashDelta: number;
}

/** Cash in pocket by the target under each path. COGS on this stock is already
 * sunk, so cash collected is sales revenue (margin plus recovered cost on units
 * sold) less storage; unsold units are not cash. */
function computeCash(
  result: ClearanceLadderResult,
  unitCost: number
): CashComparison {
  const holdLadder = result.holdingCostTotal ?? 0;
  const holdBaseline = result.baselineHoldingCostTotal ?? 0;
  const cashLadder =
    result.marginCaptured + result.unitsCleared * unitCost - holdLadder;
  const cashBaseline =
    result.baselineMarginCaptured +
    result.baselineUnitsCleared * unitCost -
    holdBaseline;
  return { cashLadder, cashBaseline, cashDelta: cashLadder - cashBaseline };
}

function Result({
  result,
  unitsRemaining,
  unitCost,
  tiers,
  weeksToTarget,
  targetDateObj,
  now,
  formatCurrency,
  formatUnits,
}: {
  result: ClearanceLadderResult;
  unitsRemaining: number;
  unitCost: number;
  tiers: DiscountTiers;
  weeksToTarget: number;
  targetDateObj: Date;
  now: Date;
  formatCurrency: (v: number) => string;
  formatUnits: (v: number) => string;
}) {
  const cash = computeCash(result, unitCost);

  return (
    <div className="flex flex-col gap-5">
      <Headline
        result={result}
        cash={cash}
        weeksToTarget={weeksToTarget}
        targetDateObj={targetDateObj}
        now={now}
        formatUnits={formatUnits}
        formatCurrency={formatCurrency}
      />

      {/* Chart takes two thirds, the worth-it comparison the last third. */}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <DepletionChart
            result={result}
            unitsRemaining={unitsRemaining}
            weeksToTarget={weeksToTarget}
            targetDateObj={targetDateObj}
            formatUnits={formatUnits}
          />
        </div>
        <div className="lg:col-span-1">
          <Comparison
            result={result}
            cash={cash}
            unitCost={unitCost}
            targetDateObj={targetDateObj}
            formatCurrency={formatCurrency}
            formatUnits={formatUnits}
          />
        </div>
      </div>

      {result.status === "ok" && (
        <ScheduleStrip result={result} tiers={tiers} now={now} />
      )}
    </div>
  );
}

/** The takeaway line, led by the number an operator cares about: does marking
 * down beat riding it out, and by how much cash. */
function Headline({
  result,
  cash,
  weeksToTarget,
  targetDateObj,
  now,
  formatUnits,
  formatCurrency,
}: {
  result: ClearanceLadderResult;
  cash: CashComparison;
  weeksToTarget: number;
  targetDateObj: Date;
  now: Date;
  formatUnits: (v: number) => string;
  formatCurrency: (v: number) => string;
}) {
  const heading =
    "text-heading-24 lg:text-heading-32 leading-tight text-balance text-[#0D0D0D]";
  const money = (v: number) => formatCurrency(Math.abs(v));

  // No reached step actually discounts: the "markdown" is identical to riding
  // it out, so don't claim it clears faster or nets a different amount.
  if (result.phases.every((p) => p.discountPct === 0)) {
    return (
      <div>
        <p className={heading}>{STRINGS.noDiscountHeadline}</p>
        <p className="text-body-16 text-text-label mt-2 text-pretty">
          You&rsquo;ll be left with about{" "}
          <span className="font-semibold text-[#0D0D0D]">
            {formatUnits(result.unitsAtTarget)} {STRINGS.units}
          </span>{" "}
          by <span className="whitespace-nowrap">{fmtLong(targetDateObj)}</span>
          . Raise a discount step above to clear more.
        </p>
      </div>
    );
  }

  // Full-price pace already clears in time: riding it out is the answer.
  if (result.baselineClears && result.baselineClearWeek !== null) {
    const clearDate = dateFromWeeks(now, result.baselineClearWeek);
    return (
      <div>
        <p className={heading}>
          Ride it out. You&rsquo;ll clear at full price by{" "}
          <span className="text-pulse-700 whitespace-nowrap">
            {fmtLong(clearDate)}
          </span>
          .
        </p>
        <p className="text-body-16 text-text-label mt-2">
          A markdown here only gives up margin. Use the steps below only if you
          need the cash in sooner.
        </p>
      </div>
    );
  }

  const clears = result.clearsByTarget;
  const clearDate = dateFromWeeks(now, result.clearWeek ?? weeksToTarget);
  const ladderWins = cash.cashDelta >= 1;

  return (
    <div>
      <p className={heading}>
        {ladderWins ? "Mark it down." : "A markdown clears it faster."}{" "}
        {clears ? (
          <>
            Clear out by{" "}
            <span className="text-pulse-700 whitespace-nowrap">
              {fmtLong(clearDate)}
            </span>
          </>
        ) : (
          <>
            Down to{" "}
            <span className="text-pulse-700 whitespace-nowrap">
              {formatUnits(result.unitsAtTarget)} {STRINGS.units}
            </span>{" "}
            by{" "}
            <span className="text-pulse-700 whitespace-nowrap">
              {fmtLong(targetDateObj)}
            </span>
          </>
        )}
        .
      </p>
      <p className="text-body-16 text-text-label mt-2 text-pretty">
        {ladderWins ? (
          <>
            That puts about{" "}
            <span className="text-pulse-700 font-semibold">
              {money(cash.cashDelta)}
            </span>{" "}
            more in your pocket than riding it out
            {!clears &&
              ", and you can go deeper or push the date to clear the rest"}
            .
          </>
        ) : (
          <>
            But riding it out at full price nets about{" "}
            <span className="font-semibold text-[#0D0D0D]">
              {money(cash.cashDelta)}
            </span>{" "}
            more by then. Mark down only if you need the cash sooner.
          </>
        )}
      </p>
    </div>
  );
}

/** The honest head-to-head, compact: cash in pocket by the target under the
 * markdown versus riding it out, with the supporting figures beneath. */
function Comparison({
  result,
  cash,
  unitCost,
  targetDateObj,
  formatCurrency,
  formatUnits,
}: {
  result: ClearanceLadderResult;
  cash: CashComparison;
  unitCost: number;
  targetDateObj: Date;
  formatCurrency: (v: number) => string;
  formatUnits: (v: number) => string;
}) {
  const ladderWins = cash.cashDelta >= 0;
  interface Row {
    label: string;
    ladder: string;
    nothing: string;
    /** Render the ladder value in red inside brackets (a cost, not a gain). */
    cost?: boolean;
  }
  const rows: Row[] = [
    {
      label: STRINGS.rowStockCleared,
      ladder: formatUnits(result.unitsCleared),
      nothing: formatUnits(result.baselineUnitsCleared),
    },
    {
      label: STRINGS.rowStockLeft,
      ladder: formatCurrency(result.unitsAtTarget * unitCost),
      nothing: formatCurrency(result.baselineUnitsAtTarget * unitCost),
    },
    {
      label: STRINGS.rowMargin,
      ladder: formatCurrency(result.marginCaptured),
      nothing: formatCurrency(result.baselineMarginCaptured),
    },
    {
      label: STRINGS.rowDiscountCost,
      ladder: `(${formatCurrency(result.discountCost)})`,
      nothing: formatCurrency(0),
      cost: true,
    },
  ];
  if (
    result.holdingCostTotal !== null &&
    result.baselineHoldingCostTotal !== null
  ) {
    rows.push({
      label: STRINGS.rowStorage,
      ladder: formatCurrency(result.holdingCostTotal),
      nothing: formatCurrency(result.baselineHoldingCostTotal),
    });
  }

  return (
    <div className="bg-surface border-pulse-100 flex h-full flex-col rounded-xl border p-4">
      <p className="text-body-16 font-semibold text-[#0D0D0D]">
        {STRINGS.compareTitle}
      </p>
      <p className="text-label-11 text-grey-400 mt-3 text-pretty uppercase">
        {STRINGS.cashHeroLabel}{" "}
        <span className="whitespace-nowrap">{fmtLong(targetDateObj)}</span>
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-body-14 text-pulse-700">
            {STRINGS.compareLadder}
          </span>
          <span
            className={cn(
              "text-heading-24 font-semibold",
              ladderWins ? "text-pulse-700" : "text-[#0D0D0D]"
            )}
          >
            {formatCurrency(cash.cashLadder)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-body-14 text-grey-400">
            {STRINGS.compareNothing}
          </span>
          <span className="text-heading-20 text-grey-400 font-semibold">
            {formatCurrency(cash.cashBaseline)}
          </span>
        </div>
      </div>

      <div className="border-pulse-100 mt-3 grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 border-t pt-3">
        {rows.map((row) => (
          <Fragment key={row.label}>
            <span className="text-body-14 text-text-label">{row.label}</span>
            <span
              className={cn(
                "text-body-14 text-right font-semibold",
                row.cost ? "text-red-700" : "text-[#0D0D0D]"
              )}
            >
              {row.ladder}
            </span>
            <span className="text-body-14 text-grey-400 text-right">
              {row.nothing}
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** Compact three-step schedule. Always renders all three tiers so the layout
 * never jumps; steps the schedule never reaches are greyed with a "not needed"
 * tag rather than removed. */
function ScheduleStrip({
  result,
  tiers,
  now,
}: {
  result: ClearanceLadderResult;
  tiers: DiscountTiers;
  now: Date;
}) {
  const order = ["start", "mid", "final"] as const;
  return (
    <div>
      <p className="text-body-sm mb-2 font-semibold text-[#0D0D0D]">
        {STRINGS.scheduleTitle}
      </p>
      <div className="grid grid-cols-3 gap-3">
        {order.map((tier) => {
          const phase = result.phases.find((p) => p.tier === tier);
          const active = phase !== undefined;
          const discountPct = phase ? phase.discountPct : tiers[tier];
          const date =
            tier === "start"
              ? STRINGS.phaseNowLabel
              : phase
                ? fmtLong(dateFromWeeks(now, phase.weekStart))
                : STRINGS.notNeededTag;
          return (
            <div
              key={tier}
              className={cn(
                "rounded-xl border p-3 text-center transition-opacity",
                active
                  ? "bg-surface border-pulse-100"
                  : "border-grey-100 bg-surface opacity-45"
              )}
            >
              <p className="text-label-11 text-grey-400 uppercase">
                {STRINGS.phaseNames[tier]}
              </p>
              <p className="text-heading-24 lg:text-heading-32 text-pulse-700 mt-1">
                {Math.round(discountPct)}%
              </p>
              <p className="text-body-14 text-text-label mt-0.5">{date}</p>
              {phase?.belowCost && (
                <p className="text-body-14 mt-0.5 inline-flex items-center gap-1 text-red-700">
                  <TriangleAlert className="size-3.5" aria-hidden="true" />
                  below cost
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A dependency-free inline SVG of the depletion curve: the do-nothing baseline
 * against the laddered schedule, with the gap between them shaded to show the
 * extra stock the ladder clears. Colours come from CSS variables so they track
 * the site theme. */
function DepletionChart({
  result,
  unitsRemaining,
  weeksToTarget,
  targetDateObj,
  formatUnits,
}: {
  result: ClearanceLadderResult;
  unitsRemaining: number;
  weeksToTarget: number;
  targetDateObj: Date;
  formatUnits: (v: number) => string;
}) {
  const W = 640;
  const H = 300;
  const padL = 46;
  const padR = 16;
  const padT = 16;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const x = (week: number) => padL + (week / weeksToTarget) * plotW;
  const y = (units: number) => padT + (1 - units / unitsRemaining) * plotH;

  const toPath = (pts: { week: number; units: number }[]) =>
    pts
      .map(
        (p, i) =>
          `${i === 0 ? "M" : "L"} ${x(p.week).toFixed(1)} ${y(p.units).toFixed(1)}`
      )
      .join(" ");

  const ladderLine = toPath(result.curve);
  const baselineLine = toPath(result.baselineCurve);

  // The gap between the two lines: forward along the ladder, back along the
  // baseline. This is the extra stock the ladder clears over doing nothing.
  const ladderPts = result.curve.map(
    (p) => `${x(p.week).toFixed(1)} ${y(p.units).toFixed(1)}`
  );
  const baselinePts = result.baselineCurve.map(
    (p) => `${x(p.week).toFixed(1)} ${y(p.units).toFixed(1)}`
  );
  const gapPath =
    ladderPts.length && baselinePts.length
      ? `M ${ladderPts.join(" L ")} L ${baselinePts.toReversed().join(" L ")} Z`
      : "";

  // Horizontal gridlines at 0/25/50/75/100% of the starting units.
  const gridUnits = [0, 0.25, 0.5, 0.75, 1].map((f) => unitsRemaining * f);

  // Phase segments: each discount runs from its start to the next phase (or
  // target). Drawn as light dividers + a centred discount label, not a band.
  const segments = result.phases.map((phase, i) => {
    const next = result.phases[i + 1];
    const startW = phase.weekStart;
    const endW = next ? next.weekStart : weeksToTarget;
    return { startW, endW, discountPct: phase.discountPct };
  });

  const clearWeek = result.clearWeek;

  return (
    <figure className="m-0">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <LegendItem
          color="var(--grey-400)"
          dashed
          label={STRINGS.chartBaseline}
        />
        <LegendItem color="var(--pulse-700)" label={STRINGS.chartLadder} />
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Depletion chart. Doing nothing leaves about ${formatUnits(result.baselineUnitsAtTarget)} units by the target. The markdown ladder ${result.clearsByTarget ? "clears the stock" : `leaves about ${formatUnits(result.unitsAtTarget)} units`}.`}
      >
        {/* Gridlines + y labels */}
        {gridUnits.map((u, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y(u)}
              y2={y(u)}
              stroke="var(--grey-100)"
              strokeWidth={1}
            />
            <text
              x={padL - 6}
              y={y(u) + 3}
              textAnchor="end"
              className="text-[10px]"
              fill="var(--grey-400)"
            >
              {formatUnits(u)}
            </text>
          </g>
        ))}

        {/* Shaded gap between the two lines */}
        {gapPath && <path d={gapPath} fill="var(--pulse-700)" opacity={0.12} />}

        {/* Phase dividers + discount labels */}
        {segments.map((s, i) => (
          <g key={i}>
            {i > 0 && (
              <line
                x1={x(s.startW)}
                x2={x(s.startW)}
                y1={padT}
                y2={padT + plotH}
                stroke="var(--grey-100)"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            )}
            <text
              x={(x(s.startW) + x(s.endW)) / 2}
              y={padT + 12}
              textAnchor="middle"
              className="text-[10px] font-semibold"
              fill="var(--pulse-700)"
            >
              {Math.round(s.discountPct)}%
            </text>
          </g>
        ))}

        {/* Baseline line (do nothing) */}
        <path
          d={baselineLine}
          fill="none"
          stroke="var(--grey-400)"
          strokeWidth={2}
          strokeDasharray="6 5"
          strokeLinecap="round"
        />

        {/* Ladder line */}
        <path
          d={ladderLine}
          fill="none"
          stroke="var(--pulse-700)"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Cleared marker */}
        {clearWeek !== null && clearWeek <= weeksToTarget && (
          <g>
            <circle cx={x(clearWeek)} cy={y(0)} r={4} fill="var(--pulse-700)" />
            <text
              x={Math.min(x(clearWeek), W - padR - 30)}
              y={y(0) - 8}
              textAnchor="middle"
              className="text-[10px] font-semibold"
              fill="var(--pulse-700)"
            >
              {STRINGS.clearedTag}
            </text>
          </g>
        )}

        {/* X labels: now and the target date */}
        <text
          x={padL}
          y={H - 10}
          textAnchor="start"
          className="text-[10px]"
          fill="var(--grey-400)"
        >
          {STRINGS.phaseNowLabel}
        </text>
        <text
          x={W - padR}
          y={H - 10}
          textAnchor="end"
          className="text-[10px]"
          fill="var(--grey-400)"
        >
          {fmtLong(targetDateObj)}
        </text>
      </svg>
    </figure>
  );
}

function LegendItem({
  color,
  label,
  dashed,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <span className="text-body-14 text-text-label flex items-center gap-1.5">
      <svg width="16" height="8" aria-hidden="true">
        <line
          x1="0"
          y1="4"
          x2="16"
          y2="4"
          stroke={color}
          strokeWidth={2.5}
          strokeDasharray={dashed ? "4 3" : undefined}
          strokeLinecap="round"
        />
      </svg>
      {label}
    </span>
  );
}

function NumberField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      {hint && <p className="text-body-14 text-text-label mb-2">{hint}</p>}
      <Input
        id={id}
        inputMode="numeric"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-14 ${INPUT_TEXT}`}
      />
    </div>
  );
}

function MoneyField({
  label,
  hint,
  value,
  symbol,
  placeholder,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  symbol: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      {hint && <p className="text-body-14 text-text-label mb-2">{hint}</p>}
      <div className="relative">
        <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn("placeholder:text-grey-400 h-14 pl-9", INPUT_TEXT)}
        />
      </div>
    </div>
  );
}

function DateField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      {hint && <p className="text-body-14 text-text-label mb-2">{hint}</p>}
      <input
        type="date"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-body-16 border-grey-100 focus-visible:outline-pulse-700 h-14 w-full rounded-lg border bg-white px-4 text-[#0D0D0D] focus-visible:outline-2 focus-visible:-outline-offset-1"
      />
    </div>
  );
}

function TierField({
  label,
  value,
  inactive,
  atCostPct,
  onChange,
}: {
  label: string;
  value: number;
  inactive?: boolean;
  /** Discount % at which the sale price meets unit cost; a tick marks it on
   * the track. Null when it can't be placed within 0-100. */
  atCostPct?: number | null;
  onChange: (v: number) => void;
}) {
  const id = useId();
  const { min, max, step } = PERCENT_RANGE;
  const showCostMark =
    atCostPct !== null &&
    atCostPct !== undefined &&
    atCostPct > 0 &&
    atCostPct < 100;
  return (
    <div className={cn("transition-opacity", inactive && "opacity-45")}>
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={id}
          className="text-body-sm flex items-center gap-2 font-semibold text-[#0D0D0D]"
        >
          {label}
          {inactive && (
            <span className="text-label-11 text-grey-400 font-medium normal-case">
              {STRINGS.notNeededTag}
            </span>
          )}
        </label>
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            aria-label={label}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
            className={`h-10 w-20 [appearance:textfield] pr-7 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${INPUT_TEXT}`}
          />
          <span className="text-text-label pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-base">
            %
          </span>
        </div>
      </div>
      <div className="relative mt-2">
        <SliderRoot
          aria-label={label}
          min={min}
          max={max}
          step={step}
          value={[value]}
          onValueChange={(vals) => onChange(clamp(vals[0] ?? min, min, max))}
          rangeClassName="bg-pulse-700"
          className="py-2"
        >
          <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
          {showCostMark && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 z-10 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-600"
              style={{ left: `${atCostPct}%` }}
            />
          )}
        </SliderRoot>
        {showCostMark && (
          <span
            aria-hidden="true"
            className="text-label-11 pointer-events-none absolute top-full -translate-x-1/2 whitespace-nowrap text-red-700"
            style={{ left: `${atCostPct}%` }}
          >
            at cost
          </span>
        )}
      </div>
    </div>
  );
}
